import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Settings, Key, Mail, Shield, Database, Globe } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface SystemSetting {
  id: string;
  key: string;
  value: any;
  description: string | null;
  is_public: boolean;
}

interface SettingsState {
  platform_name: string;
  support_email: string;
  trial_days_default: number;
  charity_enabled: boolean;
  stripe_enabled: boolean;
  maintenance_mode: boolean;
  registration_enabled: boolean;
  email_notifications: boolean;
  push_notifications: boolean;
}

export function SystemSettingsModule() {
  const [settings, setSettings] = useState<SettingsState>({
    platform_name: "Herzensempfehlung",
    support_email: "support@herzensempfehlung.de",
    trial_days_default: 14,
    charity_enabled: true,
    stripe_enabled: true,
    maintenance_mode: false,
    registration_enabled: true,
    email_notifications: true,
    push_notifications: true,
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from("system_settings")
        .select("*");

      if (error) throw error;

      const settingsObj: any = {};
      (data || []).forEach((setting: SystemSetting) => {
        settingsObj[setting.key] = setting.value;
      });

      setSettings(prev => ({ ...prev, ...settingsObj }));
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast({
        title: "Fehler",
        description: "Einstellungen konnten nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key: string, value: any) => {
    try {
      const { error } = await supabase
        .from("system_settings")
        .upsert({
          key,
          value,
          description: getSettingDescription(key),
          is_public: isPublicSetting(key),
        });

      if (error) throw error;
    } catch (error) {
      console.error(`Error updating ${key}:`, error);
      throw error;
    }
  };

  const getSettingDescription = (key: string): string => {
    const descriptions: { [key: string]: string } = {
      platform_name: "Name der Plattform",
      support_email: "Support E-Mail Adresse",
      trial_days_default: "Standard Testphase in Tagen",
      charity_enabled: "Wald/Spenden Feature aktiviert",
      stripe_enabled: "Stripe Zahlungen aktiviert",
      maintenance_mode: "Wartungsmodus aktiviert",
      registration_enabled: "Neue Registrierungen erlaubt",
      email_notifications: "E-Mail Benachrichtigungen aktiviert",
      push_notifications: "Push Benachrichtigungen aktiviert",
    };
    return descriptions[key] || "";
  };

  const isPublicSetting = (key: string): boolean => {
    const publicSettings = ["platform_name", "support_email"];
    return publicSettings.includes(key);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      // Update each setting individually
      await Promise.all(
        Object.entries(settings).map(([key, value]) => updateSetting(key, value))
      );

      toast({
        title: "Erfolgreich",
        description: "Einstellungen wurden gespeichert.",
      });
    } catch (error) {
      console.error("Error saving settings:", error);
      toast({
        title: "Fehler",
        description: "Einstellungen konnten nicht gespeichert werden.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (key: keyof SettingsState, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>System- & Sicherheitseinstellungen</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          System- & Sicherheitseinstellungen
        </CardTitle>
        <CardDescription>
          Verwalten Sie globale System-Einstellungen und Sicherheitskonfiguration
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="general" className="space-y-6">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="general" className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Allgemein
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Sicherheit
            </TabsTrigger>
            <TabsTrigger value="integrations" className="flex items-center gap-2">
              <Key className="h-4 w-4" />
              Integrationen
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Benachrichtigungen
            </TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Plattform-Einstellungen</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="platform_name">Plattform-Name</Label>
                  <Input
                    id="platform_name"
                    value={settings.platform_name}
                    onChange={(e) => handleInputChange("platform_name", e.target.value)}
                    placeholder="Name der Plattform"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="support_email">Support E-Mail</Label>
                  <Input
                    id="support_email"
                    type="email"
                    value={settings.support_email}
                    onChange={(e) => handleInputChange("support_email", e.target.value)}
                    placeholder="support@example.com"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="trial_days">Standard Testphase (Tage)</Label>
                <Input
                  id="trial_days"
                  type="number"
                  value={settings.trial_days_default}
                  onChange={(e) => handleInputChange("trial_days_default", parseInt(e.target.value))}
                  placeholder="14"
                  min="0"
                />
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="font-medium">Feature-Aktivierung</h4>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Wartungsmodus</Label>
                    <p className="text-sm text-muted-foreground">
                      Plattform für Wartungsarbeiten sperren
                    </p>
                  </div>
                  <Switch
                    checked={settings.maintenance_mode}
                    onCheckedChange={(checked) => handleInputChange("maintenance_mode", checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Neue Registrierungen</Label>
                    <p className="text-sm text-muted-foreground">
                      Neue Benutzer-Registrierungen erlauben
                    </p>
                  </div>
                  <Switch
                    checked={settings.registration_enabled}
                    onCheckedChange={(checked) => handleInputChange("registration_enabled", checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Wald/Spenden-Feature</Label>
                    <p className="text-sm text-muted-foreground">
                      Umwelt- und Spendenfunktionen aktivieren
                    </p>
                  </div>
                  <Switch
                    checked={settings.charity_enabled}
                    onCheckedChange={(checked) => handleInputChange("charity_enabled", checked)}
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="security" className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Sicherheits-Einstellungen</h3>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="h-5 w-5 text-yellow-600" />
                  <span className="font-medium text-yellow-800">Sicherheitshinweise</span>
                </div>
                <div className="text-sm text-yellow-700 space-y-1">
                  <p>• API-Keys regelmäßig rotieren</p>
                  <p>• Zwei-Faktor-Authentifizierung für Superadmins aktivieren</p>
                  <p>• Regelmäßige Sicherheits-Audits durchführen</p>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium">Rollen & Berechtigungen</h4>
                
                <div className="border rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium">Superadmin</div>
                      <div className="text-sm text-muted-foreground">Vollzugriff auf alle Funktionen</div>
                    </div>
                    <span className="text-sm text-green-600">Aktiv</span>
                  </div>
                  
                  <Separator />
                  
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium">Admin</div>
                      <div className="text-sm text-muted-foreground">Verwaltung des eigenen Tenants</div>
                    </div>
                    <span className="text-sm text-green-600">Aktiv</span>
                  </div>
                  
                  <Separator />
                  
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium">Customer</div>
                      <div className="text-sm text-muted-foreground">Basis-Benutzerfunktionen</div>
                    </div>
                    <span className="text-sm text-green-600">Aktiv</span>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="integrations" className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">API-Integrationen</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Stripe Zahlungen</Label>
                    <p className="text-sm text-muted-foreground">
                      Stripe für Subscription-Zahlungen aktivieren
                    </p>
                  </div>
                  <Switch
                    checked={settings.stripe_enabled}
                    onCheckedChange={(checked) => handleInputChange("stripe_enabled", checked)}
                  />
                </div>

                <Separator />

                <div className="space-y-3">
                  <h4 className="font-medium">API-Keys Status</h4>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between items-center p-3 border rounded">
                      <div className="flex items-center gap-2">
                        <Key className="h-4 w-4" />
                        <span>Stripe API Key</span>
                      </div>
                      <span className="text-sm text-green-600">Konfiguriert</span>
                    </div>
                    
                    <div className="flex justify-between items-center p-3 border rounded">
                      <div className="flex items-center gap-2">
                        <Key className="h-4 w-4" />
                        <span>Resend API Key</span>
                      </div>
                      <span className="text-sm text-green-600">Konfiguriert</span>
                    </div>
                    
                    <div className="flex justify-between items-center p-3 border rounded">
                      <div className="flex items-center gap-2">
                        <Key className="h-4 w-4" />
                        <span>VAPID Keys</span>
                      </div>
                      <span className="text-sm text-green-600">Konfiguriert</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="notifications" className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Benachrichtigungs-Einstellungen</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>E-Mail Benachrichtigungen</Label>
                    <p className="text-sm text-muted-foreground">
                      System-E-Mails an Admins und Kunden senden
                    </p>
                  </div>
                  <Switch
                    checked={settings.email_notifications}
                    onCheckedChange={(checked) => handleInputChange("email_notifications", checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Push Benachrichtigungen</Label>
                    <p className="text-sm text-muted-foreground">
                      Browser-Push-Notifications aktivieren
                    </p>
                  </div>
                  <Switch
                    checked={settings.push_notifications}
                    onCheckedChange={(checked) => handleInputChange("push_notifications", checked)}
                  />
                </div>

                <Separator />

                <div className="space-y-3">
                  <h4 className="font-medium">E-Mail Konfiguration</h4>
                  
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <Label>SMTP Server Status</Label>
                      <div className="p-3 border rounded bg-green-50 border-green-200">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="text-sm text-green-700">Resend SMTP: Aktiv</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end pt-6">
          <Button onClick={saveSettings} disabled={saving}>
            {saving ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Speichern...
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4" />
                Einstellungen speichern
              </div>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}